import { inject } from "@angular/core";
import { signalStore, withState, withMethods, patchState, withHooks } from "@ngrx/signals";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { APP_CONFIG } from "@application/config";
import { DxOdataStoreService } from "@shared/components/dx-data-grid/data-access";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import { tapResponse } from "@ngrx/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { SettingsPostsService } from "../infra/posts.service";
import { IPost } from "../entities/posts.model";
import { DataSource } from "devextreme/common/data";

export interface ISettingsPosts {
    showViewModal: boolean;
    showEditModal: boolean;
    allPosts: IPost[];
    post: IPost;
    postsToDisplay: IPost[];
    currentPostIndex: number;
    isAutoDisplayMode: boolean;
}

export const SettingsPostsInitialStore: ISettingsPosts = {
    showViewModal: false,
    showEditModal: false,
    allPosts: [],
    post: null,
    postsToDisplay: [],
    currentPostIndex: 0,
    isAutoDisplayMode: false
};

export const SettingsPostsStore = signalStore(
    { providedIn: 'root' },
    withState(SettingsPostsInitialStore),
    withMethods((
        store,
        postsService = inject(SettingsPostsService),
        odataStore = inject(DxOdataStoreService),
        url = inject(APP_CONFIG).apiUrl,
        errorStore = inject(ErrorStore)) => {

        /**
         * Helper function to filter posts that should be displayed based on localStorage and time
         */
        const filterPostsToDisplay = (posts: IPost[]): IPost[] => {
            const now = new Date();
            const currentHour = now.getHours();
            const isMorning = currentHour < 15;
            
            return posts.filter(post => {
                const morningKey = `morningConfirmation-${post.PostagemId}`;
                const nightKey = `nightConfirmation-${post.PostagemId}`;
                
                if (isMorning) {
                    return !localStorage.getItem(morningKey);
                } else {
                    return !localStorage.getItem(nightKey);
                }
            });
        }
        return {
            toggleShowViewModal: (showViewModal = !store.showViewModal()) => {
                patchState(store, { showViewModal, isAutoDisplayMode: false });
            },

            toggleShowEditModal: (showEditModal = !store.showEditModal()) => {
                patchState(store, { showEditModal });
            },

            getPostsDataSource: (): DataSource => {
                return odataStore.fromOdata({
                url: url.odata,
                metadata: 'PostagensActuar',
                key: 'PostagemId',
                params: {
                    $select: 'Titulo,Texto,DataInicioPostagem,DataFimPostagem,UrlConteudo,PostagemId'
                }
            });
            },

            getPost: rxMethod<string>(pipe(
            tap(() => patchState(store, setPropLoading('getPost'))),
            switchMap((payload) => postsService.getPost(payload).pipe(
                tapResponse({
                next: (post) => {
                    patchState(store, { post, ...setPropLoaded('getPost') })
                },
                error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('getPost', error));
                }
                })
            )))),

            getAllPostagens: rxMethod<void>(pipe(
                tap(() => patchState(store, setPropLoading('getAllPostagens'))),
                switchMap(() => postsService.getAllPostagens().pipe(
                    tapResponse({
                        next: (allPosts) => {
                            patchState(store, { allPosts }, setPropLoaded('getAllPostagens'));
                            const postsToDisplay = filterPostsToDisplay(allPosts);
                            patchState(store, { postsToDisplay, currentPostIndex: 0 });
                            
                            if (postsToDisplay?.length > 0) {
                                patchState(store, { 
                                    post: postsToDisplay[0],
                                    showViewModal: true,
                                    isAutoDisplayMode: true
                                });
                            }
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, setPropError('getAllPostagens', error));
                        }
                    })
                ))
            )),

            addPostagem: rxMethod<IPost>(pipe(
                tap(() => patchState(store, setPropLoading('addPostagem'))),
                switchMap((post) => postsService.addPostagem(post).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, setPropLoaded('addPostagem'))
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.handleError({ error, title: 'SETTINGS.POSTS.FORM.ALERT.CREATE.ERROR_TITLE' });
                            patchState(store, setPropError('addPostagem', error));
                        }
                    })
                ))
            )),

            updatePostagem: rxMethod<IPost>(pipe(
                tap(() => patchState(store, setPropLoading('updatePostagem'))),
                switchMap((post) => postsService.updatePostagem(post).pipe(
                    tapResponse({
                        next: (updatedPost) => {
                            patchState(store, setPropLoaded('updatePostagem'))
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.handleError({ error, title: 'SETTINGS.POSTS.FORM.ALERT.UPDATE.ERROR_TITLE' });
                            patchState(store, setPropError('updatePostagem', error));
                        }
                    })
                ))
            )),

            removePostagem: rxMethod<string>(pipe(
                tap(() => patchState(store, setPropLoading('removePostagem'))),
                switchMap((postId) => postsService.deletePostagem(postId).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, setPropLoaded('removePostagem'))
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.handleError({ error, title: 'SETTINGS.POSTS.FORM.ALERT.DELETE.ERROR_TITLE' });
                            patchState(store, setPropError('removePostagem', error));
                        }
                    })
                ))
            )),

            blankPostState: () => {
                patchState(store, { post: null }, setPropInit('getPost'));
            },

            setPropsInit(props: string[]): void {
                patchState(store, { ...setPropInit(...props) });
            },

            /**
             * Marks a post as viewed in localStorage based on time of day
             */
            markPostAsViewed(postId: string): void {
                const now = new Date();
                const currentHour = now.getHours();
                const isAfternoon = currentHour >= 15;
                const key = isAfternoon
                    ? `nightConfirmation-${postId}` 
                    : `morningConfirmation-${postId}`;
                
                localStorage.setItem(key, 'true');
                
                const currentIndex = store.currentPostIndex();
                const nextIndex = currentIndex + 1;
                const postsToDisplay = store.postsToDisplay();
                
                if (nextIndex < postsToDisplay.length) {
                    patchState(store, { 
                        currentPostIndex: nextIndex,
                        post: postsToDisplay[nextIndex],
                        showViewModal: true,
                        isAutoDisplayMode: true
                    });
                } else {
                    patchState(store, { showViewModal: false, post: null, isAutoDisplayMode: false });
                }
            },

            /**
             * Initializes post display logic
             */
            initializePostDisplay(): void {
                if (window.innerWidth > 800) {
                    const postsToDisplay = filterPostsToDisplay(store.allPosts());
                    patchState(store, { postsToDisplay, currentPostIndex: 0 });
                    
                    if (postsToDisplay?.length > 0) {
                        patchState(store, { 
                            post: postsToDisplay[0],
                            showViewModal: true,
                            isAutoDisplayMode: true
                        });
                    }
                }
            },

            /**
             * Gets the current post to display
             */
            getCurrentPost(): IPost | null {
                const posts = store.postsToDisplay();
                const index = store.currentPostIndex();
                return posts?.[index] ?? null;
            }
        }
    }),
    withReqState('getPost', 'getAllPostagens', 'addPostagem', 'updatePostagem', 'removePostagem'),
    withHooks({
        onInit(store) {
          store.getAllPostagens();
        }
    })
);
